import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import numbro from 'numbro';
import { shallow } from 'zustand/shallow';

import { LoadingSpinner } from '../../components/Molecules';
import { WEATeacherMembershipProgramDetailsTemplate } from '../../components/Templates';
import { WEATeacherPlanPrice } from '../../constants/exchangeRates';
import { RegionsCode } from '../../constants/region';
import { AppToastErrorOption } from '../../constants/toast';
import { useShareData } from '../../context';
import useDoWhile, { DoWhileFn } from '../../hooks/useDoWhile';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import { TranslationLocale } from '../../i18n';
import { UiLocale } from '../../models/Base';
import {
  ReferralSystem,
  RewardRedemption,
  RewardRedemptionState,
  RewardRedemptionTabState,
} from '../../models/RewardStore';
import { ResetPasswordResult } from '../../models/UserStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getUiLocale } from '../../utils/i18n';
import {
  getLocalCurrencyByRegion,
  isValidRegionCode,
} from '../../utils/region';

interface State {
  referralCode: string;
  lastReferralCode: string;
  disabled: boolean;
  rewardRedemptionId: string | undefined;
  isLoading: boolean;
  errorMessage: string;
  isFocused: boolean;
  validationLoading: boolean;
  isValid: boolean;
  hasCancelled: boolean;
  isFirstChecked: boolean;
  isNeedFetch: boolean;
  rewardReferralCode: string | undefined;
}

const {
  fetchRedemption,
  createRedemption,
  cancelRedemption,
  referralCodeValidation,
  fetchRewardReferral,
} = useRewardStore.getState();

const WEATeacherMembershipProgramDetailsScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { sharedData, setSharedData } = useShareData();
  const userId = useSessionStore((store) => store.activeUserId);
  const activeRegion = useSessionStore((store) => store.activeRegion);

  const userAccount = useUserStore((state) => state.getAccount(userId ?? ''));
  const userProfile = useUserStore((state) => state.getProfile(userId ?? ''));

  const { region: paramRegion } = useParams();

  const [state, setState] = useState<State>({
    referralCode: '',
    lastReferralCode: '',
    disabled: false,
    rewardRedemptionId: undefined,
    isLoading: false,
    errorMessage: '',
    isFocused: false,
    validationLoading: false,
    isValid: false,
    hasCancelled: false,
    isFirstChecked: false,
    isNeedFetch: false,
    rewardReferralCode: undefined,
  });

  useEffect(() => {
    if (paramRegion && isValidRegionCode(paramRegion)) {
      useSessionStore.getState().setActiveRegion(paramRegion as RegionsCode);
    }
  }, [paramRegion]);

  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? getLocalCurrencyByRegion(activeRegion);
  }, [activeRegion, userProfile?.localCurrency]);

  useEffect(() => {
    if (userId) {
      fetchRewardReferral(
        { referralSystem: ReferralSystem.Joyajoy }, // WeaMembershipPlan
        { headers: { 'Cache-Control': 'no-cache' } },
      ).catch(console.error);
    }
  }, [userId]);

  const query = new URLSearchParams(location.search);

  const redemptionId = query.get('rewardRedemptionId');
  const referralCode = query.get('referralCode');

  useEffect(() => {
    setSharedData({
      pathName: location.pathname,
      searchReferralCode: referralCode ? referralCode : '',
    });
  }, [location.pathname, referralCode, setSharedData]);

  const rewardReferral = useRewardStore((store) =>
    store.getRewardReferral(
      `${ReferralSystem.Joyajoy}_${userId}` ?? '', // WeaMembershipPlan
    ),
  );

  const fieldName = `system_${RewardRedemptionTabState.Paying}`;
  const collectionField = useRewardStoreListField('redemptions', fieldName);

  const fetchRewardRedemptions = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore
        .getState()
        .fetchRedemptions('system', RewardRedemptionTabState.Paying, cursor),
    [],
  );

  const { fetchAll: fetchAllRewardRedemptions } = useFetchList(
    collectionField,
    fetchRewardRedemptions,
  );

  useEffect(() => {
    if (
      (redemptionId === null && userId && !state.hasCancelled) ||
      state.isNeedFetch
    ) {
      fetchAllRewardRedemptions().catch(console.error);
    }
  }, [
    fetchAllRewardRedemptions,
    redemptionId,
    userId,
    state.hasCancelled,
    state.isNeedFetch,
  ]);

  const redemptions = useRewardStore((rewardState) => {
    return rewardState
      .getRedemptions(fieldName)
      .reduce<Pick<RewardRedemption, 'rewardRedemptionId' | 'rewardOfferId'>[]>(
        (acc, offer) => {
          if (offer.state === RewardRedemptionState.Paying) {
            acc.push({
              rewardRedemptionId: offer.rewardRedemptionId,
              rewardOfferId: offer.rewardOfferId,
            });
          }
          return acc;
        },
        [],
      );
  }, arrayEqual(shallow));

  const payingRedemption = useMemo(() => {
    return redemptions.find((item) => item);
  }, [redemptions]);

  const getReferralCode = useCallback(async () => {
    const referralCodeData = await useRewardStore
      .getState()
      .getRewardReferralCode(ReferralSystem.Joyajoy); // WeaMembershipPlan
    if (referralCodeData) {
      setState((s) => ({
        ...s,
        rewardReferralCode: referralCodeData.referralCode,
      }));
    }
  }, []);

  useEffect(() => {
    if (userId) {
      getReferralCode().catch(console.error);
    }
  }, [getReferralCode, userId]);

  const checkReferralCode = useCallback(async () => {
    if (state.referralCode === state.lastReferralCode && state.isFirstChecked) {
      return;
    }
    if (
      state.referralCode.trim().length > 0 &&
      state.referralCode === state.rewardReferralCode
    ) {
      setState((s) => ({
        ...s,
        isValid: false,
        errorMessage: t('Invalid Code. This is your referral code.'),
      }));
      return;
    }
    if (state.referralCode) {
      setState((s) => ({ ...s, validationLoading: true }));
      const res = await referralCodeValidation(
        { referralSystem: ReferralSystem.Joyajoy }, // WeaMembershipPlan
        { referralCode: state.referralCode },
      );
      setState((s) => ({
        ...s,
        validationLoading: false,
        isFirstChecked: true,
        lastReferralCode: state.referralCode,
      }));
      if (res === ResetPasswordResult.Success) {
        setState((s) => ({
          ...s,
          isValid: true,
          disabled: true,
          errorMessage: '',
        }));
        setSharedData({
          ...sharedData,
          referralCode: state.referralCode,
        });
      } else {
        setState((s) => ({
          ...s,
          isValid: false,
          errorMessage: t('Please check your referral code and try again.'),
        }));
        setSharedData({
          ...sharedData,
          referralCode: '',
          searchReferralCode: '',
        });
      }
    } else {
      setState((s) => ({ ...s, isValid: false, errorMessage: '' }));
    }
  }, [
    setSharedData,
    sharedData,
    state.isFirstChecked,
    state.lastReferralCode,
    state.referralCode,
    state.rewardReferralCode,
    t,
  ]);

  useEffect(() => {
    const code =
      rewardReferral?.rewardReferralCode?.referralCode ?? referralCode;
    if (code) {
      setState((s) => ({
        ...s,
        disabled: true,
        referralCode: code,
      }));
    }
  }, [referralCode, rewardReferral?.rewardReferralCode?.referralCode]);

  useEffect(() => {
    if (state.disabled || !state.isFirstChecked) {
      checkReferralCode().catch(() => {});
    }
  }, [checkReferralCode, state.disabled, state.isFirstChecked]);

  useEffect(() => {
    if (
      (redemptionId !== null ||
        payingRedemption?.rewardRedemptionId !== undefined) &&
      !state.hasCancelled
    ) {
      const idToCancel: string =
        redemptionId || payingRedemption?.rewardRedemptionId || '';

      cancelRedemption('system', idToCancel)
        .then(() => {
          setState((s) => ({ ...s, hasCancelled: true }));
        })
        .catch(() => {
          setState((s) => ({ ...s, hasCancelled: true }));
        });
    }
  }, [
    state.hasCancelled,
    navigate,
    payingRedemption?.rewardRedemptionId,
    redemptionId,
    state.referralCode,
  ]);

  const loadRedemption = useDoWhile(
    useCallback<DoWhileFn>(
      async (signal) => {
        if (state.rewardRedemptionId !== undefined) {
          const res = await fetchRedemption(
            'system',
            state.rewardRedemptionId,
            { signal },
          );
          if (res) {
            switch (res.state) {
              case RewardRedemptionState.Paying:
                {
                  const url = res.payment?.url;
                  if (url !== undefined) {
                    setState((s) => ({ ...s, isLoading: false }));
                    window.location.href = url;
                  }
                }
                return true;
              case RewardRedemptionState.Fulfilled:
                // navigate(
                //   `/wea-member/rewardRedemption/${state.rewardRedemptionId}?success=true`,
                // );
                return true;
              case RewardRedemptionState.Failed:
                // navigate(  // failed navigation controlled at backend
                //   `/wea-member/?rewardRedemptionId=${state.rewardRedemptionId}`,
                // );
                return false;
            }
          }
        }
        await new Promise((resolve) => setTimeout(resolve, 3000));
        return false;
      },
      [state.rewardRedemptionId],
    ),
  );

  useEffect(() => {
    if (state.rewardRedemptionId !== undefined) {
      loadRedemption().catch(() => {});
    }
  }, [loadRedemption, redemptionId, state.rewardRedemptionId]);

  const onClickReferral = useCallback(() => {
    setTimeout(() => {
      window.scroll({ top: -1, left: 0, behavior: 'smooth' });
    }, 10);
    navigate('/wea-member/referral-detail');
  }, [navigate]);

  const onClickHere = useCallback(() => {
    setTimeout(() => {
      window.scroll({ top: -1, left: 0, behavior: 'smooth' });
    }, 10);
    navigate('/wea-member/referral');
  }, [navigate]);

  const onClickJoyajoy = useCallback(() => {
    window.open('https://joyajoy.com/');
  }, []);

  const onClickWEA = useCallback(() => {
    const lang = getUiLocale(i18n.language as TranslationLocale);
    switch (lang) {
      case UiLocale.CN:
        window.open('https://worldeducators.org/zh-hans');
        break;
      case UiLocale.HK:
      case UiLocale.TW:
        window.open('https://worldeducators.org/zh-hant');
        break;
      case UiLocale.JP:
      case UiLocale.US:
      default:
        window.open('https://worldeducators.org/');
        break;
    }
  }, [i18n.language]);

  const onClickPurchase = useCallback(() => {
    if (!userId) {
      navigate('/wea-member/start');
    } else {
      setState((s) => ({ ...s, isLoading: true }));
      createRedemption('system', {
        rewardOfferId: 'wea-membership-plan',
        quantity: 1,
        paymentGateway: 'stripe',
        referralCode: state.referralCode,
      })
        .then((res) => {
          if (res) {
            setState((s) => ({
              ...s,
              rewardRedemptionId: res.rewardRedemptionId,
            }));
          } else {
            setState((s) => ({ ...s, isLoading: false }));
            toast(
              t('We are cancelling the previous order... Please try again.'),
              AppToastErrorOption,
            );
            setState((s) => ({ ...s, isNeedFetch: true }));
          }
        })
        .catch(() => {
          setState((s) => ({ ...s, isLoading: false }));
          toast(
            t('We are cancelling the previous order... Please try again.'),
            AppToastErrorOption,
          );
          setState((s) => ({ ...s, isNeedFetch: true }));
        });
    }
  }, [userId, navigate, state.referralCode, t]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        const url = new URL(window.location.href);
        if (url.search) {
          url.search = '';
          window.history.replaceState(null, '', url.toString());
          window.location.reload(); // Reload the page when navigating back
        } else {
          window.location.reload(); // Reload the page when navigating back
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const onReferralCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setState((s) => ({ ...s, referralCode: event.target.value }));
    },
    [],
  );

  const handleFocus = () => {
    setState((s) => ({ ...s, isFocused: true }));
  };

  const handleBlur = () => {
    setState((s) => ({ ...s, isFocused: false }));
    checkReferralCode().catch(() => {});
  };

  return (
    <>
      <WEATeacherMembershipProgramDetailsTemplate
        originalYearCurrency={`${currency}${numbro(
          WEATeacherPlanPrice[currency].originalPrice,
        ).format({ thousandSeparated: true })}`}
        originalMonthCurrency={`${currency}${numbro(
          WEATeacherPlanPrice[currency].originalPrice / 12,
        ).format({ thousandSeparated: true, mantissa: 2 })}`}
        referralYearCurrency={
          state.isValid
            ? `${currency}${numbro(
                WEATeacherPlanPrice[currency].referralPrice,
              ).format({ thousandSeparated: true })}`
            : `${currency}${numbro(
                WEATeacherPlanPrice[currency].specialPrice,
              ).format({ thousandSeparated: true })}`
        }
        referralMonthCurrency={
          state.isValid
            ? `${currency}${numbro(
                WEATeacherPlanPrice[currency].referralPrice / 12,
              ).format({ thousandSeparated: true, mantissa: 2 })}`
            : `${currency}${numbro(
                WEATeacherPlanPrice[currency].specialPrice / 12,
              ).format({ thousandSeparated: true, mantissa: 2 })}`
        }
        referralCode={{
          value: state.referralCode,
          onChange: onReferralCodeChange,
          disabled: state.disabled,
          errorMessage: state.errorMessage,
          onFocus: handleFocus,
          onBlur: handleBlur,
        }}
        onClickPurchase={onClickPurchase}
        validationLoading={state.validationLoading}
        purchasedPlan={userAccount?.hasActiveWeaMembership ?? false}
        onClickReferral={onClickReferral}
        onClickJoyajoy={onClickJoyajoy}
        onClickWEA={onClickWEA}
        onClickHere={onClickHere}
        isReferred={state.isValid}
      />
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </>
  );
};

export default WEATeacherMembershipProgramDetailsScreen;
